import React from 'react';
import * as Sentry from '@sentry/browser';
import { useAuthState, constants, useAuthActions } from '@/utils/auth';
import MaybeRedirect from '@/components/Layout/MaybeRedirect';
import AuthForm from '@/modules/app/AuthForm';
import AuthConfirmEmailForm from '@/modules/app/AuthConfirmEmailForm';
import AuthResetPasswordForm from '@/modules/app/AuthResetPasswordForm';
import { AuthCompleteProfile } from '@/modules/app/AuthCompleteProfile';
import { Redirect } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { Button } from '@/components/ui/button';
import { BilderitLogoHorizontal } from '@/components/BilderitLogoHorizontal';

const LogOn = () => {
  const { matches, error } = useAuthState();
  const { signOut, retry } = useAuthActions();
  React.useEffect(() => {
    if (error)
      Sentry.withScope((scope) => {
        scope.setTag('AppCrashed', 'false');
        scope.setTag('ups/reintentar', 'true');
        Sentry.captureException(error);
      });
  }, [error]);

  return matches({ [constants.STATE_SIGNED_IN]: 'error' }) ? (
    <Card
      style={{
        alignItems: 'center',
      }}
    >
      <CardHeader className="text-center">
        <CardTitle>Error</CardTitle>
        <CardDescription>Ups!.. algo salio mal</CardDescription>
      </CardHeader>
      <CardContent className="flex w-full text-center gap-6 justify-around">
        <Button
          onClick={() => {
            signOut();
          }}
          variant="link"
        >
          Salir
        </Button>
        <Button onClick={retry}>Reintentar</Button>
      </CardContent>
    </Card>
  ) : (
    <LoadingMessageCard message="Iniciando sesión" />
  );
};

const AuthPage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { matches, destination } = useAuthState();
  const { clearDestination } = useAuthActions();

  React.useEffect(() => {
    // clear the destination if we're performing the redirect below
    if (matches({ [constants.STATE_LOGGED_IN]: 'idle' }) && destination)
      clearDestination();
  }, [clearDestination, destination, matches]);

  return (
    <>
      {matches({ [constants.STATE_LOGGED_IN]: 'idle' }) ? (
        destination ? (
          <Redirect to={destination} />
        ) : (
          <>{children}</>
        )
      ) : (
        <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <a
              href="/"
              className="flex items-center gap-2 self-center font-medium"
            >
              <BilderitLogoHorizontal className="h-12" />
            </a>
            {matches(constants.STATE_SIGNED_IN) && <LogOn />}
            {!matches(constants.STATE_SIGNED_IN) && (
              <Card>
                {matches(constants.STATE_COMPLETE_PROFILE) && (
                  <>
                    <AuthCompleteProfile />
                  </>
                )}
                {matches(constants.STATE_SIGNIN) && (
                  <>
                    <MaybeRedirect to={'/login'} keepSearchParams />
                    <AuthForm />
                  </>
                )}
                {matches(constants.STATE_SIGNUP) && (
                  <>
                    <MaybeRedirect to={'/signup'} />
                    <AuthForm />
                  </>
                )}
                {matches(constants.STATE_CONFIRM_EMAIL) && (
                  <>
                    <MaybeRedirect to={'/confirm'} />
                    <AuthConfirmEmailForm />
                  </>
                )}
                {matches(constants.STATE_FORGOT_PASSWORD) && (
                  <>
                    <MaybeRedirect to={'/forgot'} />
                    <AuthResetPasswordForm />
                  </>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default AuthPage;
